import Button from '@cpg-its/ads.common.button';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { TcsmModal } from '../../../../../global/components/tcsm-modal';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { ArcRoutes } from '../../../constants/routes';
import { TerminationProps } from '../../../models/arc-epi-props';
import { Employment, EmploymentState } from '../../employment/employment-model';
import { getInitialEmploymentTypes } from '../employment-termination-helpers';
import { TerminationTypes } from '../models/employment-termination-model';
import { EmploymentCombinationTypes, terminationFormValidationSchema, TerminationFormValues } from '../models/termination-form-model';
import { EmploymentDeathFields } from './employment-death-fields';
import { EmploymentTerminationFields } from './employment-termination-fields';
import { EmploymentTerminationSummary } from './termination-employment-summary';

interface Props {
	labelSettings: LabelSettingProps;
	terminationProps: TerminationProps;
	employmentTypeCombo: EmploymentCombinationTypes;
	stipEmployment: Employment;
	nonStipEmployment: Employment;
	onSubmit: (values: TerminationFormValues) => any;
	isLoa?: boolean;
}
export function EmploymentTerminationForm(props: Props) {
	const { terminationProps, labelSettings, employmentTypeCombo, stipEmployment, nonStipEmployment, onSubmit } = props;

	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const employeeSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.EmployeeSearch}`);
	const terminationPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.EmploymentTermination}`);
	const loaPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.UnpaidLeaveOfAbsence}`);
	const history = useHistory();

	const defaultFormValue: TerminationFormValues = {
		termination_type: props?.isLoa ? TerminationTypes.LeaveOfAbsence : '',
		termination_date: null,
		employment_types: getInitialEmploymentTypes(employmentTypeCombo),
		person_retiring_indicator: '',
		extenuating_circumstances: null,
		acknowledge: false,
	};

	const [previousFormValue, setPreviousFormValue] = useState(defaultFormValue);
	const [currentFormValue, setcurrentFormValue] = useState(null);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const apiErrors = useApiResponseError(FormTypes.TERMINATION);

	const initialTouched = {};
	if (apiErrors) {
		Object.keys(apiErrors)?.forEach(
			a => {
				initialTouched[a] = true;
			},
		);
	}

	const initialValues: TerminationFormValues = currentFormValue || defaultFormValue;

	const validationSchema = terminationFormValidationSchema(labelSettings, terminationProps, apiErrors);

	const terminationTypeOptions = [
		{
			text: terminationProps?.employmentOptionLabel,
			value: TerminationTypes.Employment,
		},
		{
			text: terminationProps?.deathOptionLabel,
			value: TerminationTypes.Death,
		},
		{
			text: terminationProps?.neverWorkedHereOptionLabel,
			value: TerminationTypes.NeverWorkedHere,
		},
	];

	const nonStipOnlyTerminationTypeOptions = [
		{
			text: terminationProps?.employmentOptionLabel,
			value: TerminationTypes.Employment,
		},
		{
			text: terminationProps?.deathOptionLabel,
			value: TerminationTypes.Death,
		},
	];

	const onTerminationTypeChange = (e, formProps: FormikProps<TerminationFormValues>) => {
		const { value } = e.target;

		if (props?.isLoa && value !== TerminationTypes.LeaveOfAbsence) {
			history.push(`${terminationPath}?type=${value}`);
		}

		if (!props?.isLoa && value === TerminationTypes.LeaveOfAbsence) {
			history.push(`${loaPath}?type=${value}`);
		}

		formProps.resetForm({
			values: {
				...initialValues,
				termination_type: value,
			},
		});
	};

	const renderFormFields = (formProps: FormikProps<TerminationFormValues>) => {
		const { termination_type } = formProps.values;

		if (termination_type === TerminationTypes.Employment || termination_type === TerminationTypes.LeaveOfAbsence) {
			return <EmploymentTerminationFields
				formProps={formProps}
				terminationProps={terminationProps}
				employmentTypeCombo={employmentTypeCombo}
				setPreviousValue={setPreviousFormValue}
				currentValue={previousFormValue}
				setCurrentValue={setcurrentFormValue}
			/>;
		}

		if (termination_type === TerminationTypes.Death) {
			return <EmploymentDeathFields
				formProps={formProps}
				terminationProps={terminationProps}
			/>;
		}

		return null;
	};

	const onModalClose = () => {
		setShowConfirmModal(false);
	};

	const initialSubmit = (values: TerminationFormValues, formikHelpers: FormikHelpers<TerminationFormValues>) => {
		if (!showConfirmModal) {
			setShowConfirmModal(true);
			formikHelpers.setValues(values);
			formikHelpers.setSubmitting(false);
			return;
		}

		setShowConfirmModal(false);
		onSubmit(values);
	};

	return (
		<>
			<Formik
				initialValues={initialValues}
				onSubmit={initialSubmit}
				validationSchema={validationSchema}
				initialErrors={apiErrors}
				enableReinitialize
				initialTouched={initialTouched}
			>
				{(formProps: FormikProps<TerminationFormValues>) => {

					return (
						<>
							<Form>
								<FormWrapper
									formikProps={formProps}
									cancelText={terminationProps?.cancelButtonText}
									submitText={terminationProps?.submitButtonText}
									generalErrorMessage={terminationProps?.generalErrorMessage}
									onCancel={() => history.push(employeeSelect)}
									submitDisabled={
										!formProps.values.termination_type ||
										(formProps.values.termination_type === TerminationTypes.Employment && formProps.values.employment_types?.length < 1)
									}
									preventButtonDisableValidation={formProps.values.termination_type === TerminationTypes.NeverWorkedHere}
									initialValue={previousFormValue}
								>
									<ContentSpacer>
										<WysiwygWithReactLink content={props?.isLoa ? terminationProps?.leaveOfAbsencePageDescription : terminationProps?.employmentTerminationMessage} />
									</ContentSpacer>
									<EmploymentTerminationSummary
										employmentTypeCombo={employmentTypeCombo}
										stipEmployment={stipEmployment}
										nonStipEmployment={nonStipEmployment}
										currentEmployee={employmentState?.currentEmployee}
										isLoa={props?.isLoa}
									/>

									{
										!props.isLoa &&
										<ContentSpacer>
											<FormGroup>
												<FormField
													name='termination_type'
													label={props?.isLoa ? terminationProps?.loaDropdownLabel : terminationProps?.terminationTypeLabel}
													component='input'
													options={nonStipEmployment && !stipEmployment ? nonStipOnlyTerminationTypeOptions : terminationTypeOptions}
													errors={formProps.errors}
													touched={formProps.touched}
													select
													required
													onChange={(e) => onTerminationTypeChange(e, formProps)}
												/>
											</FormGroup>
										</ContentSpacer>
									}
									{renderFormFields(formProps)}


								</FormWrapper>
							</Form>

							<TcsmModal
								open={showConfirmModal}
								handleClose={onModalClose}>
								<>
									<p>{terminationProps?.confirmTerminationModalDescription}</p>
									<ButtonWrapper flexEnd>
										<Button type='button' buttonStyle='tertiary' text={terminationProps?.modalCancelButtonText} backgroundColor='#808080' onClick={onModalClose} />
										<Button
											type='button'
											buttonStyle='primary'
											text={terminationProps?.modalConfirmButtonText}
											backgroundColor='#0000FF'
											onClick={() => formProps.submitForm()} />
									</ButtonWrapper>
								</>

							</TcsmModal>
						</>
					);
				}}
			</Formik>
		</>
	);
}

EmploymentTerminationForm.defaultProps = {
	isLoa: false,
};